// src/lib/supabase/userAddresses.ts
import { supabase } from './client';
import type { NoteLocation } from '@/types/location';

export interface UserAddress {
  id: string;
  user_id: string;
  label: string;
  location: NoteLocation;
  is_primary: boolean;
  altura?: string;
  streetName?: string;
  floor?: string;
  apartment?: string;
  notes?: string;
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

function mapUserAddressRow(row: any): UserAddress {
  return {
    id: row.id,
    user_id: row.user_id,
    label: row.label,
    is_primary: row.is_primary,
    location: {
      displayName: row.location_display_name,
      latitude: row.latitude,
      longitude: row.longitude,
    },
    altura: row.altura,
    streetName: row.street_name ?? row.streetName,
    floor: row.floor,
    apartment: row.apartment,
    notes: row.notes,
    salutation: row.salutation,
    firstName: row.first_name,
    lastName: row.last_name,
  };
}

function isMissingOptionalPersonColumnError(error: any): boolean {
  const message = error?.message || '';
  return (
    message.includes('user_addresses') &&
    (
      message.includes("'salutation'") ||
      message.includes("'first_name'") ||
      message.includes("'last_name'") ||
      message.includes("'street_name'") ||
      message.includes("'streetName'") ||
      message.includes("'altura'")
    )
  );
}

function hasMissingOptionalColumnError(error: any): boolean {
  return isMissingOptionalPersonColumnError(error);
}

export async function getUserAddresses(userId: string): Promise<UserAddress[]> {
  const { data, error } = await supabase
    .from('user_addresses')
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false });
  if (error) throw error;
  if (!data) return [];
  return data.map(mapUserAddressRow);
}

export async function upsertUserAddress(address: Omit<UserAddress, 'id'> & { id?: string }): Promise<UserAddress> {
  // Descomponer location en columnas separadas
  const {
    id,
    user_id,
    label,
    is_primary,
    location,
    altura,
    streetName,
    floor,
    apartment,
    notes,
    salutation,
    firstName,
    lastName,
    // reference removed
  } = address;
  const baseRow: any = {
    user_id,
    label,
    is_primary: typeof is_primary === 'boolean' ? is_primary : false,
    location_display_name: location.displayName,
    latitude: location.latitude,
    longitude: location.longitude,
    floor,
    apartment,
    notes,
  };
  const rowWithAddressOptional = {
    ...baseRow,
    ...(altura !== undefined ? { altura } : {}),
    ...(streetName !== undefined ? { street_name: streetName } : {}),
  };
  const hasOptionalPersonFields = salutation !== undefined || firstName !== undefined || lastName !== undefined;
  const rowWithOptionalPersonFields = {
    ...rowWithAddressOptional,
    ...(salutation !== undefined ? { salutation } : {}),
    ...(firstName !== undefined ? { first_name: firstName } : {}),
    ...(lastName !== undefined ? { last_name: lastName } : {}),
  };
  if (id) baseRow.id = id;
  if (id) rowWithAddressOptional.id = id;
  if (id) rowWithOptionalPersonFields.id = id;

  const attempts: any[] = [];
  attempts.push(hasOptionalPersonFields ? rowWithOptionalPersonFields : rowWithAddressOptional);
  if (hasOptionalPersonFields) {
    attempts.push(rowWithAddressOptional);
  }
  attempts.push(baseRow);

  let data: any = null;
  let error: any = null;
  for (let index = 0; index < attempts.length; index += 1) {
    const result = await supabase
      .from('user_addresses')
      .upsert([attempts[index]], { onConflict: 'id' })
      .select();
    data = result.data;
    error = result.error;

    if (!error) {
      break;
    }
    const canRetry = index < attempts.length - 1;
    if (!(canRetry && hasMissingOptionalColumnError(error))) {
      break;
    }
  }

  if (error) throw error;
  const rowData = Array.isArray(data) ? data[0] : data;
  return mapUserAddressRow(rowData);
}

export async function deleteUserAddress(id: string): Promise<void> {
  const { error } = await supabase
    .from('user_addresses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}
